// @ts-nocheck
import { checkboxSizes, CheckboxStyle, StyleProps } from "@insite/mobius/Checkbox";
import { IconWrapper } from "@insite/mobius/Icon";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import CheckboxChecked from "@store/Components/Toolkit/Icons/Checkbox/CheckboxChecked";
import CheckboxIndeterminate from "@store/Components/Toolkit/Icons/Checkbox/CheckboxIndeterminate";
import { CheckboxGroupStyleProps } from "@store/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import { checkboxDescriptionSpacingStyles } from "@store/Themes/common/mixins/checkbox";
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { focusStyles } from "@store/Themes/common/mixins/common";
import { css } from "styled-components";

/* ------------------------------------------------------------------------------------------ */
/* Related Extensions => src/Extensions/Mobius/Checkbox ------------------------------------- */
/* ------------------------------------------------------------------------------------------ */

type CheckboxColor = string | null;
const checkboxStyles = (color?: CheckboxColor, backgroundColor?: CheckboxColor, borderColor?: CheckboxColor) => {
    return css<StyleProps>`
        ${({ theme, _sizeVariant }) => {
            const { borderWidth } = checkboxSizes[_sizeVariant] as any;

            return css`
                border: ${pxToRem(borderWidth)} solid transparent;
                ${cssProp("color", color, x => resolveColor(x, theme))}
                ${cssProp("background-color", backgroundColor, x => resolveColor(x, theme))}
                ${cssProp("border-color", borderColor, x => resolveColor(x, theme))}
            `;
        }}
    `;
};

const checkbox: Styleguide["checkbox"] = {
    defaultProps: {
        iconProps: {
            src: CheckboxChecked,
        },
        indeterminateIconProps: {
            src: CheckboxIndeterminate,
        },
        css: css<StyleProps>`
            ${({ _sizeVariant }) => {
                const { borderWidth, boxSpacing, offset, toggleOffset } = checkboxSizes[_sizeVariant] as any;

                return css`
                    cursor: default;

                    && [role="checkbox"] {
                        cursor: pointer;

                        + label {
                            margin: 0;
                            padding-left: ${pxToRem(boxSpacing)};
                            word-break: break-word;
                            cursor: pointer;
                        }

                        &[aria-disabled="true"] {
                            cursor: default;

                            + label {
                                color: ${getColor("common.borderDark")}; /* Store Custom Code */
                                cursor: default;
                            }
                        }

                        &:focus {
                            box-shadow: none;
                        }
                    }

                    span[role="checkbox"] {
                        ${focusStyles(`-${pxToRem(borderWidth)}`, pxToRem(borderWidth))}
                        ${checkboxStyles(
                            "primary",
                            "common.background",
                            "common.accentContrast" /* Store Custom Code */,
                        )}
                        margin-top: ${pxToRem(offset)};

                        &[aria-checked="true"]:not([aria-disabled="true"]),
                        &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                            ${checkboxStyles("common.background" /* Store Custom Code */, "primary", "primary")}
                        }

                        &[aria-disabled="true"] {
                            ${checkboxStyles(
                                "common.disabledDark" /* Store Custom Code */,
                                "common.disabledDark",
                                "common.borderDark" /* Store Custom Code */,
                            )}

                            &[aria-checked="true"],
                            &[aria-checked="mixed"] {
                                ${checkboxStyles("common.background", "common.disabledDark", "common.disabledDark")}
                            }
                        }
                    }

                    [class^="SliderFrame"] {
                        ${focusStyles(true)}
                        margin-top: ${pxToRem(toggleOffset)};
                        background: ${getColor("common.borderDark")};
                        color: ${getColor("common.background")};
                        border-color: transparent;

                        &[aria-checked="true"] {
                            background: ${getColor("primary")};
                            color: ${getColor("primary.contrast")};
                        }

                        &[aria-disabled="true"] {
                            background: ${getColor("common.disabledDark")};
                            color: ${getColor("common.background")};
                        }

                        ${IconWrapper} {
                            &:first-child,
                            &:last-child {
                                visibility: hidden;
                            }
                        }
                    }
                `;
            }}
        `,
    },
    groupDefaultProps: {
        css: css<CheckboxGroupStyleProps>`
            ${checkboxDescriptionSpacingStyles()}
            ${({ _sizeVariant = "default" }) => {
                const { itemSpacing } = checkboxSizes[_sizeVariant] as any;

                return css`
                    ${CheckboxStyle} + ${CheckboxStyle} {
                        margin-top: ${pxToRem(itemSpacing)};
                    }
                `;
            }}
        ` as any,
    },
};

export default checkbox;
